/**
 * Choosing which lists a segment is the union of.
 *
 * The control is a set of checkboxes, and what it produces is a **set**: the
 * API takes the full desired membership on every save rather than an add/remove
 * pair, so this component never has to reason about deltas — the checked boxes
 * are the answer.
 *
 * Three details that are not incidental:
 *
 * - **Every list is offered, not only the finished ones.** A list still
 *   importing contributes nothing today and will contribute later, because a
 *   segment references its lists rather than copying their rows. Hiding it
 *   would make a legitimate choice invisible; showing it with its status is the
 *   honest explanation for a member sitting at zero.
 * - **The sheet is shown beside the name.** One workbook becomes one list per
 *   worksheet and they share a file name — without the sheet, two rows here
 *   read as duplicates of each other, and telling them apart is the entire
 *   reason they were kept separate at import.
 * - **Ticks survive paging.** Selection is held by the caller as whole records,
 *   not as ids into the current page, so a list ticked on page one still counts
 *   while page two is on screen. The footer says so when it happens, because a
 *   total that includes something you cannot see needs explaining.
 *
 * The footer's two clauses are the two things a reader gets wrong without them,
 * and both are about a number that is not what it looks like: a selection count
 * larger than the ticks visible on this page, and a member sitting at "none
 * yet" because its import has not finished. The *reason* a still-importing list
 * may be ticked — a segment references rather than copies — is the second
 * bullet above, and it stays there rather than on the screen.
 *
 * Slice 66 changed one thing here, the count's face: sans with tabular
 * figures, as every count in the product now is, because a mono zero is
 * dotted and reads as an 8 at this size. "none yet" stays mono caps — it is a
 * word in a figure's place and should look like one. The Blueprint's "build
 * from" chips were considered for this control and rejected; the reason is in
 * `NewSegmentPage`'s header.
 */
import {
  DEFAULT_PAGE_SIZE,
  datasetSortSchema,
  type DatasetSummary,
  type SegmentDataset,
} from '@wend/shared';
import {
  Checkbox,
  DataTable,
  buttonVariants,
  type DataTableColumn,
  type DataTableSort,
} from '@wend/ui';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { DatasetStatusBadge } from '../../components/DatasetStatusBadge';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { listDatasets } from '../../lib/api/datasets';
import { errorMessage } from '../../lib/api/errors';
import { datasetSheetLabel } from '../../lib/dataset-display';
import type { FormErrors } from '../../lib/form';
import { formatCount, pluralise } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';

/**
 * The one message a screen shows about the selection.
 *
 * The API reports a dataset id it will not accept against that id's *position*
 * (`datasetIds.0`), which is right for a machine and useless above a table of
 * checkboxes — there is no field at index 0 to attach it to. Any of them, or a
 * message about the list as a whole, surfaces as one line.
 */
export function membershipError(errors: FormErrors): string | undefined {
  const direct = errors.datasetIds;
  if (direct !== undefined) return direct;

  const indexed = Object.keys(errors).find((key) => key.startsWith('datasetIds.'));
  return indexed === undefined ? undefined : errors[indexed];
}

/**
 * What a list with no contacts to contribute says instead of a number.
 *
 * The distinction is small and it is the zero-versus-not-measured rule: a list
 * that is `pending` or `processing` has **no count yet** and will have one, and
 * `none yet` is the honest reading of it. A list that `failed` has no count and
 * is not going to get one until somebody fixes the file and runs the import
 * again — `none yet` there promises an arrival that is not coming, beside a red
 * badge saying the opposite. Exported because `SegmentDetailPage`'s member rows
 * render the same figure and the two must not drift; the badge beside it is
 * what carries *which* of the states it is.
 */
export function contactsPending(status: DatasetSummary['status']): string {
  return status === 'failed' ? 'none' : 'none yet';
}

/**
 * A list as this picker holds it — the same shape the API returns for a
 * segment's members, so a segment's current membership seeds the selection
 * without any conversion and nothing about the shape is re-declared here.
 */
function toPickable(dataset: DatasetSummary): SegmentDataset {
  return {
    id: dataset.id,
    name: dataset.name,
    sheetName: dataset.sheetName,
    status: dataset.status,
    // The best count available before a segment exists. The API counts the
    // contacts themselves once there is a segment to count them for.
    contactCount: dataset.importedCount,
  };
}

export interface DatasetPickerProps {
  /** The ticked lists, as whole records so the caller can total them. */
  selected: readonly SegmentDataset[];
  onChange: (selected: SegmentDataset[]) => void;
  disabled?: boolean;
}

export function DatasetPicker({ selected, onChange, disabled = false }: DatasetPickerProps) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'name', direction: 'asc' });

  const sortField = datasetSortSchema.catch('name').parse(sort.key);

  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listDatasets(
        { page, pageSize: DEFAULT_PAGE_SIZE, sort: sortField, direction: sort.direction },
        signal,
      ),
    [page, sortField, sort.direction],
  );

  const rows = useMemo<SegmentDataset[]>(
    () => (data?.items ?? []).map(toPickable),
    [data],
  );

  const selectedIds = useMemo(() => new Set(selected.map((item) => item.id)), [selected]);

  const onPage = rows.filter((row) => selectedIds.has(row.id));
  const allOnPageTicked = rows.length > 0 && onPage.length === rows.length;
  const someOnPageTicked = onPage.length > 0 && !allOnPageTicked;

  /** Ticked lists that are not on the page in front of you. */
  const tickedElsewhere = selected.length - onPage.length;

  function toggle(row: SegmentDataset, checked: boolean): void {
    if (checked) {
      if (selectedIds.has(row.id)) return;
      onChange([...selected, row]);
      return;
    }
    onChange(selected.filter((item) => item.id !== row.id));
  }

  /**
   * The select-all. It acts on the page in front of the user and nothing else —
   * a control that silently ticked rows on pages you have never seen would be
   * the opposite of the informed choice this screen is for.
   */
  function toggleAllOnPage(checked: boolean): void {
    if (checked) {
      const missing = rows.filter((row) => !selectedIds.has(row.id));
      onChange([...selected, ...missing]);
      return;
    }
    const onPageIds = new Set(rows.map((row) => row.id));
    onChange(selected.filter((item) => !onPageIds.has(item.id)));
  }

  const columns = useMemo<Array<DataTableColumn<SegmentDataset>>>(
    () => [
      {
        key: 'select',
        width: '3.25rem',
        header: (
          <Checkbox
            size="sm"
            checked={allOnPageTicked ? true : someOnPageTicked ? 'indeterminate' : false}
            onCheckedChange={toggleAllOnPage}
            disabled={disabled || rows.length === 0}
            aria-label="Include every list on this page"
          />
        ),
        cell: (dataset) => (
          <Checkbox
            size="sm"
            checked={selectedIds.has(dataset.id)}
            onCheckedChange={(checked) => {
              toggle(dataset, checked);
            }}
            disabled={disabled}
            aria-label={`Include ${dataset.name}`}
          />
        ),
      },
      {
        key: 'name',
        header: 'List',
        sortable: true,
        cell: (dataset) => {
          const sheet = datasetSheetLabel(dataset);

          return (
            <div className="min-w-0">
              <p className="font-medium text-text">{dataset.name}</p>
              {sheet !== null && (
                <p className="flex min-w-0 items-center gap-1 font-mono text-[0.7rem] tracking-[0.04em] text-muted">
                  <FileSpreadsheet aria-hidden="true" className="size-3 shrink-0" />
                  <span className="sr-only">worksheet </span>
                  <span className="truncate">{sheet}</span>
                </p>
              )}
            </div>
          );
        },
      },
      {
        key: 'contacts',
        header: 'Contacts',
        align: 'right',
        width: '9rem',
        cell: (dataset) =>
          dataset.status === 'ready' ? (
            <span className="text-[0.86rem] tabular-nums text-text">
              {formatCount(dataset.contactCount)}
            </span>
          ) : (
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
              {contactsPending(dataset.status)}
            </span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '11rem',
        cell: (dataset) => <DatasetStatusBadge status={dataset.status} />,
      },
    ],
    // `toggle` and `toggleAllOnPage` close over the current selection, so the
    // columns have to be rebuilt whenever it — or the page under it — changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedIds, allOnPageTicked, someOnPageTicked, disabled],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  if (failedToLoad) {
    return (
      <ErrorState
        error={error}
        title="Could not load your lists"
        onRetry={reload}
        className="flex flex-col items-start gap-3 py-2"
      />
    );
  }

  const nothingUploaded = !loadingFirst && rows.length === 0 && page === 1;

  return (
    <div className="flex flex-col gap-4">
      {error !== null && (
        <FormAlert tone="error">
          {errorMessage(error)} The lists below are the last version that loaded.
        </FormAlert>
      )}

      <DataTable
        label="Lists this segment combines"
        columns={columns}
        rows={rows}
        getRowKey={(dataset) => dataset.id}
        loading={loadingFirst}
        sort={sort}
        onSortChange={setSort}
        minWidth="40rem"
        empty={nothingUploaded ? <NoLists /> : <p className="text-[0.9rem] text-muted">No lists on this page.</p>}
        {...(pagination === null
          ? {}
          : {
              pagination: {
                page: pagination.page,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onPageChange: setPage,
              },
            })}
      />

      <p className="text-[0.85rem] leading-relaxed text-muted">
        {selected.length === 0 ? (
          <>No list is ticked, so this segment is empty.</>
        ) : (
          <>
            <span className="text-text">{pluralise(selected.length, 'list')}</span> ticked
            {tickedElsewhere > 0 && (
              <>
                {' '}
                — {formatCount(tickedElsewhere)} of them on another page, still counted below
              </>
            )}
            .
          </>
        )}{' '}
        A list still importing can be ticked — its contacts join when the import finishes.
      </p>
    </div>
  );
}

function NoLists() {
  return (
    <div className="flex flex-col items-center gap-3 py-4">
      <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
        No lists to combine yet. A segment is built out of lists you have uploaded, so that is the
        first step.
      </p>
      <Link to="/audiences/upload" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
        <Upload aria-hidden="true" className="size-4" />
        Upload a list
      </Link>
    </div>
  );
}
